import { Component } from '@verndale/core';
import { timeline } from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';

class ProductFeatureImage extends Component {
  isReversed = false;
  activeIndex = 0;
  breakpoint = window.matchMedia('(min-width: 1024px)');

  constructor(el: HTMLElement) {
    super(el);

    if (this.el.classList.contains('product-feature-image--image-left')) {
      this.isReversed = true;
    }

    this.addAnimationElements();

    const links = [...(this.dom.links as NodeListOf<HTMLAnchorElement>)];
    const images = [...(this.dom.animatedImages as NodeListOf<HTMLDivElement>)];

    this.activateItem(links[this.activeIndex], images[this.activeIndex]);
  }

  setupDefaults() {
    this.dom = {
      imagesContainer: this.el.querySelector<HTMLElement>(
        '.product-feature-image__images'
      ),
      items: this.el.querySelectorAll('.product-feature-image__link'),
      images: this.el.querySelectorAll('.product-feature-image__link .picture'),
      links: this.el.querySelectorAll(
        '.product-feature-image__link a, .product-feature-image__link-inner-container'
      )
    };
  }

  addListeners() {
    (this.dom.links as NodeListOf<HTMLAnchorElement>).forEach(link => {
      link.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
      link.addEventListener('focus', this.handleMouseEnter.bind(this));
    });
  }

  addAnimationElements() {
    const links = this.dom.links as NodeListOf<HTMLAnchorElement>;
    const imagesContainer = this.dom.imagesContainer as HTMLDivElement;
    const images = this.dom.images as NodeListOf<HTMLDivElement>;

    links.forEach(link => {
      const span = document.createElement('span');
      span.classList.add('product-feature-image__link-animation');
      link.append(span);
    });

    images.forEach(image => {
      imagesContainer.appendChild(image.cloneNode(true));
    });

    this.dom.animatedImages = imagesContainer.querySelectorAll('.picture');
  }

  handleMouseEnter(e: Event) {
    if (!this.breakpoint.matches) return;
    const links = [...(this.dom.links as NodeListOf<HTMLAnchorElement>)];
    const images = [...(this.dom.animatedImages as NodeListOf<HTMLDivElement>)];
    const target = (e as MouseEvent).currentTarget as HTMLAnchorElement;
    const newIndex = links.indexOf(target);

    if (this.activeIndex === newIndex) return;

    this.deactivateItem(
      links[this.activeIndex],
      images[this.activeIndex],
      newIndex > this.activeIndex
    );
    this.activateItem(
      links[newIndex],
      images[newIndex],
      this.activeIndex > newIndex
    );
    this.activeIndex = newIndex;
  }

  activateItem(
    item: HTMLAnchorElement,
    image: HTMLDivElement,
    reverse = false
  ) {
    const border = item.querySelector<HTMLSpanElement>(
      '.product-feature-image__link-animation'
    );
    const heading = item.querySelector<HTMLHeadingElement>('h3');
    const description = item.querySelector<HTMLParagraphElement>('p');

    const sequence: TimelineDefinition = [];

    if (heading) {
      sequence.push([
        heading,
        { x: 30 },
        { duration: 0.5, at: reverse ? 0.1 : 0, easing: 'ease-in' }
      ]);
    }

    if (description) {
      sequence.push([
        description,
        { x: 30 },
        { duration: 0.5, at: reverse ? 0 : 0.1, easing: 'ease-in' }
      ]);
    }

    if (border) {
      sequence.push([
        border,
        { y: reverse ? ['102%', 0] : ['-102%', 0] },
        { duration: 0.5, at: 0, easing: 'ease-in' }
      ]);
    }

    sequence.push([
      image,
      { opacity: [0, 1], x: [this.isReversed ? '-50%' : '50%', 0] },
      { duration: 0.8, at: 0.2, easing: 'ease-in' }
    ]);

    return timeline(sequence, { duration: 0.4 });
  }

  deactivateItem(
    item: HTMLAnchorElement,
    image: HTMLDivElement,
    reverse = false
  ) {
    const border = item.querySelector<HTMLSpanElement>(
      '.product-feature-image__link-animation'
    );
    const heading = item.querySelector<HTMLHeadingElement>('h3');
    const description = item.querySelector<HTMLParagraphElement>('p');

    const sequence: TimelineDefinition = [];

    if (heading) {
      sequence.push([
        heading,
        { x: 0 },
        { duration: 0.5, at: 0.1, easing: 'ease-in' }
      ]);
    }

    if (description) {
      sequence.push([
        description,
        { x: 0 },
        { duration: 0.5, at: reverse ? 0.2 : 0, easing: 'ease-in' }
      ]);
    }

    if (border) {
      sequence.push([
        border,
        { y: reverse ? [0, '102%'] : [0, '-102%'] },
        { duration: 0.5, at: 0, easing: 'ease-in' }
      ]);
    }

    sequence.push([
      image,
      { opacity: 0, x: this.isReversed ? '-50%' : '50%' },
      { duration: 0.8, at: 0, easing: 'ease-in' }
    ]);

    return timeline(sequence, { duration: 0.4 });
  }
}

export default ProductFeatureImage;
